import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getRabbiSpecialAudioUrl, RabbiSpecialAudioType } from './utils';
import { FIREBASE_FUNCTIONS } from '../utils/firebaseFunctions';
import { convertAudioBlobToMp3 } from '../utils/mp3Converter';

type RabbiInfo = { nusach?: string; key?: string; id?: string };

const RabbiSpecialRecordingUI: React.FC<{ type: RabbiSpecialAudioType }> = ({ type }) => {
  const { t } = useTranslation();
  const [rabbiInfo, setRabbiInfo] = useState<RabbiInfo | null>(null);
  const [exists, setExists] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingBlob, setPendingBlob] = useState<Blob | null>(null);
  const [recording, setRecording] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const raw = localStorage.getItem('rabbiInfo');
    const info = raw ? (JSON.parse(raw) as RabbiInfo) : null;
    if (info) setRabbiInfo(info);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('rabbiToken');
    if (!token || !rabbiInfo) return;
    const check = async () => {
      try {
        const resp = await fetch(FIREBASE_FUNCTIONS.checkRabbiAudioExists(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ psukim: [], specialTypes: [type] }),
        });
        if (resp.ok) {
          const data = await resp.json();
          setExists(!!data.existence?.[type]);
        }
      } catch {
        // ignore
      }
    };
    void check();
  }, [type, rabbiInfo]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  useEffect(() => {
    if (!pendingBlob && !pendingFile && objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, [pendingBlob, pendingFile]);

  const uploadAudio = async (fileOrBlob: File | Blob): Promise<string> => {
    const file =
      fileOrBlob instanceof File
        ? fileOrBlob
        : new File([fileOrBlob], `recording-${type}-${Date.now()}.webm`, {
            type: fileOrBlob.type || 'audio/webm',
          });
    let mp3File = file;
    if (file.type !== 'audio/mp3' && file.type !== 'audio/mpeg') {
      const mp3Blob = await convertAudioBlobToMp3(file);
      mp3File = new File([mp3Blob], file.name.replace(/\.[^/.]+$/, '') + '.mp3', {
        type: 'audio/mp3',
      });
    }
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1] || '');
      reader.onerror = reject;
      reader.readAsDataURL(mp3File);
    });
    const token = localStorage.getItem('rabbiToken');
    if (!token) throw new Error('No token');
    const resp = await fetch(FIREBASE_FUNCTIONS.uploadRabbiAudio(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ specialType: type, audioBase64: base64 }),
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data.error || 'Upload failed');
    if (!data.url) throw new Error('No URL');
    return data.url;
  };

  const deleteAudio = async () => {
    const token = localStorage.getItem('rabbiToken');
    if (!token) throw new Error('No token');
    const resp = await fetch(FIREBASE_FUNCTIONS.deleteRabbiAudio(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ specialType: type }),
    });
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}));
      throw new Error(data.error || 'Delete failed');
    }
  };

  const startRecord = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
      recorder.onstop = () => {
        setPendingBlob(new Blob(chunks, { type: 'audio/webm' }));
        setPendingFile(null);
        setRecording(false);
      };
      recorder.start();
      setMediaRecorder(recorder);
      setRecording(true);
    } catch {
      setRecording(false);
    }
  };

  const stopRecord = () => {
    if (mediaRecorder && recording) {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach((tr) => tr.stop());
      setMediaRecorder(null);
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPendingFile(file);
      setPendingBlob(null);
    }
    e.target.value = '';
  };

  const saveRecording = async () => {
    const file = pendingFile;
    const blob = pendingBlob;
    if (!file && !blob) return;
    setSaving(true);
    try {
      await uploadAudio(file || blob!);
      setExists(true);
      setPendingFile(null);
      setPendingBlob(null);
    } catch {
      // leave pending so user can retry
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await deleteAudio();
      setExists(false);
      setPendingFile(null);
      setPendingBlob(null);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      setPlaying(false);
    } catch {
      // ignore
    }
  };

  const playUrl = (): string | undefined => {
    if (pendingBlob) {
      if (!objectUrlRef.current) objectUrlRef.current = URL.createObjectURL(pendingBlob);
      return objectUrlRef.current;
    }
    if (pendingFile) {
      if (!objectUrlRef.current) objectUrlRef.current = URL.createObjectURL(pendingFile);
      return objectUrlRef.current;
    }
    if (exists && rabbiInfo) {
      return getRabbiSpecialAudioUrl(
        type,
        rabbiInfo.nusach?.toLowerCase() || 'ashkenaz',
        rabbiInfo.key || rabbiInfo.id || '',
      );
    }
    return undefined;
  };

  const hasAudio = !!(pendingFile || pendingBlob || exists);

  const audioUrl = playUrl();

  return (
    <div className="w-full space-y-2" dir="rtl">
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        onChange={onFileChange}
        className="hidden"
      />
      {!hasAudio ? (
        <div className="flex gap-2 w-full">
          <button
            type="button"
            onClick={recording ? stopRecord : startRecord}
            className={`flex-1 min-w-0 py-3 rounded-lg border flex items-center justify-center gap-2 font-medium text-sm ${
              recording
                ? 'bg-red-600 border-red-600 text-white animate-pulse'
                : 'bg-[#132a49] border-[#132a49] text-white hover:bg-[#0f2239]'
            }`}
          >
            {recording ? t('stop') : t('rabbi_upload_record')}
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex-1 min-w-0 py-3 rounded-lg border bg-[#caa42e] border-[#caa42e] text-[#132a49] hover:bg-[#b69320] flex items-center justify-center gap-2 font-medium text-sm"
          >
            {t('rabbi_upload_upload')}
          </button>
        </div>
      ) : (
        <>
          {audioUrl && (
            <>
              <audio
                ref={audioRef}
                src={audioUrl}
                onEnded={() => setPlaying(false)}
                onPause={() => setPlaying(false)}
                onPlay={() => setPlaying(true)}
                className="hidden"
              />
              <div className="flex gap-2 w-full flex-wrap">
                <button
                  type="button"
                  onClick={() => {
                    const el = audioRef.current;
                    if (!el) return;
                    if (playing) el.pause();
                    else el.play().catch(() => setPlaying(false));
                  }}
                  className="w-10 h-10 flex-shrink-0 rounded-full bg-[#132a49] text-white flex items-center justify-center hover:bg-[#0f2239]"
                >
                  {playing ? '⏸' : '▶'}
                </button>
                {!exists && (pendingFile || pendingBlob) && (
                  <button
                    type="button"
                    onClick={saveRecording}
                    disabled={saving}
                    className="flex-1 min-w-0 py-2.5 rounded-lg bg-[#132a49] text-white font-medium text-sm hover:bg-[#0f2239] disabled:opacity-70 flex items-center justify-center"
                  >
                    {saving ? t('rabbi_upload_saving') : t('rabbi_upload_save')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={recording ? stopRecord : startRecord}
                  className="flex-1 min-w-0 py-2.5 rounded-lg bg-[#caa42e] border border-[#caa42e] text-[#132a49] font-medium text-sm hover:bg-[#b69320] flex items-center justify-center"
                >
                  {recording ? t('stop') : t('rabbi_upload_re_record')}
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1 min-w-0 py-2 rounded-lg border border-[#caa42e] text-[#132a49] text-sm flex items-center justify-center"
                >
                  {t('rabbi_upload_upload')}
                </button>
                <button
                  type="button"
                  onClick={doDelete}
                  className="flex-1 min-w-0 py-2 rounded-lg border border-red-500 text-red-700 text-sm hover:bg-red-50 flex items-center justify-center"
                >
                  {t('rabbi_upload_delete')}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default RabbiSpecialRecordingUI;
